import React, { useState, useEffect } from 'react';
import { createStyles, makeStyles, Theme, ClickAwayListener } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import padStart from 'lodash/padStart';
import clsx from 'clsx';

export type DiscountType = 'amount' | 'percentage';

interface DiscountFieldProps {
    value: number;
    discountType: DiscountType;
    maxAmount?: number;
    onChange: (value: number, type: DiscountType) => void;
    className?: string;
    hasError?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        wrapper: {
            position: 'relative',
            width: '100%'
        },
        container: {
            'display': 'flex',
            'alignItems': 'center',
            'height': 44,
            'border': '1px solid #D3D3D3',
            'borderRadius': 30,
            'backgroundColor': '#ffffff',
            'overflow': 'hidden',
            'transition': 'border-color 0.2s',

            '&:focus-within': {
                borderColor: '#000000'
            },

            '&.error': {
                borderColor: '#EF4F57'
            }
        },
        inputContainer: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 16,
            minWidth: 80
        },
        prefix: {
            fontFamily: 'Poppins',
            fontSize: 16,
            fontWeight: 400,
            color: '#000000',
            marginRight: 4,
            flexShrink: 0
        },
        input: {
            'flex': 1,
            'border': 'none',
            'outline': 'none',
            'backgroundColor': 'transparent',
            'fontFamily': 'Poppins',
            'fontSize': 16,
            'fontWeight': 400,
            'color': '#000000',
            'minWidth': 50,
            'width': '100%',
            'padding': 0,
            'textAlign': 'left',

            '&::placeholder': {
                color: '#AFAFAF'
            }
        },
        divider: {
            width: 1,
            height: 24,
            backgroundColor: '#D3D3D3',
            marginRight: 12,
            flexShrink: 0
        },
        typeSelector: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            paddingRight: 16,
            cursor: 'pointer',
            minWidth: 64,
            maxWidth: 64,
            justifyContent: 'flex-end',
            flexShrink: 0
        },
        typeLabel: {
            fontFamily: 'Poppins',
            fontSize: 16,
            fontWeight: 400,
            color: '#000000',
            userSelect: 'none',
            whiteSpace: 'nowrap'
        },
        typeLabelPercentage: {
            paddingRight: 10
        },
        chevron: {
            'fontSize': 10,
            'color': '#7A7A7A',
            'transition': 'transform 0.2s',
            'flexShrink': 0,

            '&.open': {
                transform: 'rotate(180deg)'
            }
        },
        dropdown: {
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 120,
            backgroundColor: '#ffffff',
            border: '1px solid #000000',
            borderRadius: 8,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 10000,
            overflow: 'hidden'
        },
        dropdownOption: {
            'padding': '12px 16px',
            'cursor': 'pointer',
            'fontFamily': 'Poppins',
            'fontSize': 14,
            'fontWeight': 400,
            'color': '#000000',
            'transition': 'background-color 0.2s',

            '&:hover': {
                backgroundColor: '#F8F8F8'
            }
        }
    })
);

export const DiscountField: React.FC<DiscountFieldProps> = props => {
    const classes = useStyles();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [text, setText] = useState(() => props.value.toFixed(2));

    useEffect(() => {
        setText(props.value.toFixed(2));
    }, [props.value, props.discountType]);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = event.target.value;
        const cleanValue = newValue.replace(/\D/g, '').replace(/^0+/, '');

        // Determine if we're dealing with percentage or amount
        const scale = 2; // Always 2 decimal places
        const maxValue = props.discountType === 'percentage' ? 100 : props.maxAmount || 999999;

        let formattedValue: string;

        if (cleanValue.length === 0) {
            formattedValue = '0.00';
        } else if (scale >= cleanValue.length) {
            formattedValue = `0.${padStart(cleanValue, scale, '0')}`;
        } else {
            const integerPart = cleanValue.slice(0, cleanValue.length - scale);
            const decimalPart = cleanValue.slice(cleanValue.length - scale);
            formattedValue = `${integerPart}.${decimalPart}`;
        }

        // Validate against max while typing
        const numValue = parseFloat(formattedValue);
        if (!isNaN(numValue) && numValue > maxValue) {
            // Don't allow typing beyond max
            return;
        }

        setText(formattedValue);
    };

    const onBlurHandler = (): void => {
        const numValue = parseFloat(text);

        // Validate based on type
        if (props.discountType === 'percentage' && numValue > 100) {
            setText('100.00');
            props.onChange(100, props.discountType);
        } else if (props.discountType === 'amount' && props.maxAmount && numValue > props.maxAmount) {
            setText(props.maxAmount.toFixed(2));
            props.onChange(props.maxAmount, props.discountType);
        } else {
            props.onChange(numValue, props.discountType);
        }
    };

    const handleTypeChange = (newType: DiscountType) => {
        setIsDropdownOpen(false);
        if (newType !== props.discountType) {
            // Reset value when changing type
            setText('0.00');
            props.onChange(0, newType);
        }
    };

    const onFocusHandler = (event: React.FocusEvent<HTMLInputElement>): void => {
        event.target.select();
    };

    const typeLabel = props.discountType === 'amount' ? 'USD' : '%';
    const showPrefix = props.discountType === 'amount';

    return (
        <div className={classes.wrapper}>
            <div className={clsx(classes.container, props.className, { error: !!props.hasError })}>
                <div className={classes.inputContainer}>
                    {showPrefix && <span className={classes.prefix}>$</span>}
                    <input
                        type="text"
                        value={text}
                        onChange={changeHandler}
                        onBlur={onBlurHandler}
                        onFocus={onFocusHandler}
                        className={classes.input}
                        placeholder="0.00"
                    />
                </div>

                <div className={classes.divider} />

                <div
                    className={classes.typeSelector}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    <span
                        className={clsx(
                            classes.typeLabel,
                            props.discountType === 'percentage' && classes.typeLabelPercentage
                        )}
                    >
                        {typeLabel}
                    </span>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={clsx(classes.chevron, { open: isDropdownOpen })}
                    />
                </div>
            </div>

            {isDropdownOpen && (
                <ClickAwayListener onClickAway={() => setIsDropdownOpen(false)}>
                    <div className={classes.dropdown}>
                        <div
                            className={classes.dropdownOption}
                            onClick={() => handleTypeChange('amount')}
                        >
                            USD
                        </div>
                        <div
                            className={classes.dropdownOption}
                            onClick={() => handleTypeChange('percentage')}
                        >
                            %
                        </div>
                    </div>
                </ClickAwayListener>
            )}
        </div>
    );
};

export default DiscountField;
